export type ExportDocType =
  | 'protocol'
  | 'summary'
  | 'tasks'
  | 'transcription'
  | 'video_insights';

const DOC_LABELS: Record<ExportDocType, string> = {
  protocol: 'protocol',
  summary: 'summary',
  tasks: 'tasks',
  transcription: 'transcription',
  video_insights: 'video_insights',
};

const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e',
  ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
  ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function transliterateChar(ch: string): string {
  const lower = ch.toLowerCase();
  if (lower in CYRILLIC_TO_LATIN) {
    const mapped = CYRILLIC_TO_LATIN[lower];
    return ch !== lower && mapped ? mapped.toUpperCase() : mapped;
  }
  return ch;
}

export function slugifyMeetingName(name: string, meetingId: string): string {
  const raw = (name || '').trim();
  if (!raw) return (meetingId || 'meeting').slice(0, 12);

  const parts: string[] = [];
  for (const ch of raw) {
    if (/^[a-zA-Z0-9_-]$/.test(ch)) {
      parts.push(/[a-zA-Z]/.test(ch) ? ch.toLowerCase() : ch);
    } else if (/[\s.-]/.test(ch)) {
      parts.push('_');
    } else if (ch.charCodeAt(0) > 127) {
      parts.push(transliterateChar(ch));
    }
  }

  let slug = parts.join('').replace(/_+/g, '_').replace(/^_|_$/g, '');
  slug = slug.replace(/[^a-z0-9_-]/gi, '').toLowerCase().slice(0, 48).replace(/^_|_$/g, '');
  return slug || (meetingId || 'meeting').slice(0, 12);
}

export function formatMeetingDateToken(dateStr: string): string {
  const raw = (dateStr || '').trim();
  if (!raw) return 'nodate';

  const dmy = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmy) {
    const [, day, month, year] = dmy;
    return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;

  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 8) return digits.slice(0, 8);
  return digits || 'nodate';
}

function formatTimeToken(): string {
  const now = new Date();
  const h = String(now.getHours()).padStart(2, '0');
  const m = String(now.getMinutes()).padStart(2, '0');
  const s = String(now.getSeconds()).padStart(2, '0');
  return `${h}${m}${s}`;
}

/** Шаблон: AI_{doc}_{meeting}_{date}_{time}_{version}.{ext} */
export function buildExportFilename(
  meetingName: string,
  meetingId: string,
  meetingDate: string,
  docType: ExportDocType,
  ext: 'pdf' | 'doc' | 'pptx',
): string {
  const docLabel = DOC_LABELS[docType] || docType;
  const slug = slugifyMeetingName(meetingName, meetingId);
  const dateToken = formatMeetingDateToken(meetingDate);
  const timeToken = formatTimeToken();
  return `AI_${docLabel}_${slug}_${dateToken}_${timeToken}_v1.${ext}`;
}
